import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from './api';
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from './utils/dateUtils';

interface DateFormatContextValue {
  dateFormat: string;
  timeFormat: string;
}

interface UseDateFormatReturn {
  formatDate: (date: string | Date | null | undefined) => string;
  formatDateTime: (dt: string | Date | null | undefined) => string;
  dateFormat: string;
  timeFormat: string;
}

const DateFormatContext = createContext<DateFormatContextValue>({
  dateFormat: 'DD.MM.YYYY',
  timeFormat: '24H',
});

export const DateFormatProvider = ({ children }: { children: React.ReactNode }) => {
  const [dateFormat, setDateFormat] = useState('DD.MM.YYYY');
  const [timeFormat, setTimeFormat] = useState('24H');

  useEffect(() => {
    api.get('settings/api/system/date-format/')
      .then((response: any) => {
        const data = response.data || response;
        if (data.date_format) setDateFormat(data.date_format);
        if (data.time_format) setTimeFormat(data.time_format);
      })
      .catch(() => {});
  }, []);

  return (
    <DateFormatContext.Provider value={{ dateFormat, timeFormat }}>
      {children}
    </DateFormatContext.Provider>
  );
};

/**
 * Returns format-aware formatDate and formatDateTime helpers bound to the
 * date_format / time_format stored in SystemSettings.
 *
 * Usage:
 *   const { formatDate, formatDateTime } = useDateFormat();
 */
export const useDateFormat = (): UseDateFormatReturn => {
  const { dateFormat, timeFormat } = useContext(DateFormatContext);
  const dtFormat = `${dateFormat} ${timeFormat === '12H' ? 'hh:mm A' : 'HH:mm'}`;
  return {
    formatDate: (date) => fmtDate(date, dateFormat),
    formatDateTime: (dt) => fmtDateTime(dt, dtFormat),
    dateFormat,
    timeFormat,
  };
};
